import React, { useRef } from 'react';
import useLang from '../../i18n/useLang';
import { SectionHeading } from '../site/PageParts';
import { Reveal, useInView } from '../site/motion';
import { CheckIcon } from './icons';

const PHASE_COLORS = ['bg-sky-200', 'bg-navy-300', 'bg-navy-600', 'bg-grovv', 'bg-grovv-800'];
// Relative effort per phase. Same scope both times; the second spends a larger share on testing.
const OLD_WEIGHTS = [16, 20, 36, 20, 8];
const NEW_WEIGHTS = [14, 16, 26, 34, 10];

function Track({ weights, delay = 0 }) {
  return (
    <div className="flex h-7 w-full gap-[2px]">
      {weights.map((w, i) => (
        <span key={i} className={`tl-seg block h-full ${PHASE_COLORS[i]}`} style={{ flexGrow: w, flexBasis: 0, '--d': `${delay + i * 110}ms` }} />
      ))}
    </div>
  );
}

// Two Gantt bars for the same scope: drawn in full, then the second compresses to about a quarter.
function TimelineCompare({ tl }) {
  const { t } = useLang();
  const ref = useRef(null);
  const inView = useInView(ref, { threshold: 0.45 });
  const phases = Array.isArray(tl.phases) ? tl.phases : [];

  return (
    <figure ref={ref} className={`mt-16 rounded-4xl bg-fog-50 p-6 ring-1 ring-fog-200 sm:p-10 ${inView ? 'tl-in' : ''}`}>
      <figcaption className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
        <span className="heading text-xl text-ink sm:text-2xl">{tl.title}</span>
        <span className="text-sm text-ink-500">{t('site.illustrative')}</span>
      </figcaption>
      <p className="sr-only">{tl.summary}</p>

      <div aria-hidden="true" className="mt-8 space-y-7">
        <div>
          <div className="mb-2.5 text-[0.95rem] font-medium text-ink-600">{tl.old}</div>
          <Track weights={OLD_WEIGHTS} />
        </div>
        <div>
          <div className="mb-2.5 text-[0.95rem] font-semibold text-ink">{tl.new}</div>
          <div className="relative">
            <span className="absolute inset-0 border border-dashed border-fog-300" />
            <div className="tl-compress">
              <Track weights={NEW_WEIGHTS} delay={350} />
            </div>
            <span
              className="tl-after absolute inset-y-0 flex items-center whitespace-nowrap text-[0.95rem] font-semibold text-grovv-800"
              style={{ insetInlineStart: 'calc(25% + 0.75rem)' }}
            >
              {tl.quarter}
            </span>
          </div>
        </div>
      </div>

      <ul aria-hidden="true" className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm text-ink-600">
        {phases.map((ph, i) => (
          <li key={ph} className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-sm ${PHASE_COLORS[i]}`} />
            {ph}
          </li>
        ))}
      </ul>
    </figure>
  );
}

export default function ThirdWay() {
  const { t } = useLang();
  const options = t('home.third.options', { returnObjects: true });
  const tl = t('home.third.timeline', { returnObjects: true });

  return (
    <section className="wrap py-24 sm:py-32">
      <Reveal>
        <SectionHeading title={t('home.third.title')} lead={t('home.third.lead')} />
      </Reveal>

      <ul className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-3">
        {(Array.isArray(options) ? options : []).map((o, i) => {
          const ours = i === 2;
          return (
            <Reveal
              as="li"
              key={o.title}
              delay={i * 110}
              className={`flex flex-col rounded-4xl p-7 sm:p-8 ${ours ? 'bg-white shadow-float ring-2 ring-grovv' : 'bg-fog-50 ring-1 ring-fog-200'}`}
            >
              <h3 className={`heading text-2xl ${ours ? 'text-ink' : 'text-ink-600'}`}>{o.title}</h3>
              <ul className="mt-6 space-y-3.5">
                {(Array.isArray(o.points) ? o.points : []).map((pt) => (
                  <li key={pt} className="flex gap-3 text-[1.02rem] leading-snug">
                    {ours ? (
                      <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-grovv-700" />
                    ) : (
                      <span aria-hidden="true" className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-ink-500/50" />
                    )}
                    <span className={ours ? 'font-medium text-ink' : 'text-ink-600'}>{pt}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          );
        })}
      </ul>

      {tl && typeof tl === 'object' && <TimelineCompare tl={tl} />}
    </section>
  );
}
